import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { Acknowledgements } from 'src/enums'
import { QueryError } from 'src/queries/network.utils'
import { PostAcknowledgementRequest } from 'src/queries/organizations/acknowledgements/acknowledgements.models'
import {
  getOrganizationAcknowledgements,
  postOrganizationAcknowledgement,
} from 'src/queries/organizations/acknowledgements/acknowledgements.network'
import { CustomMutationOptions } from 'src/queries/queries.utils'

/**
 * Bare `['acknowledged']` key: shared by the read + send mutation so a submit invalidates exactly what AlertBars reads.
 */
const ORG_ACKNOWLEDGEMENTS = 'acknowledged'

export const acknowledgementsQueryKeys = {
  org: () => [ORG_ACKNOWLEDGEMENTS] as const,
}

// staleTime 60s: the AlertBars gate reads the cached value on remounts within
// the minute rather than refetching.
export const acknowledgementsQueries = {
  org: () =>
    queryOptions({
      queryKey: acknowledgementsQueryKeys.org(),
      queryFn: () =>
        getOrganizationAcknowledgements(
          Acknowledgements.OwnerAdminCardManagerMigration,
        ),
      staleTime: 1000 * 60,
    }),
}

export const acknowledgementsMutations = {
  send: () =>
    mutationOptions<void, QueryError, PostAcknowledgementRequest>({
      mutationFn: (request) => postOrganizationAcknowledgement(request),
    }),
}

export function useSendOrgRolesAcknowledgement(
  options?: CustomMutationOptions<void, QueryError, PostAcknowledgementRequest>,
) {
  const queryClient = useQueryClient()
  const { onSuccess, ...restOptions } = options ?? {}
  return useMutation({
    ...acknowledgementsMutations.send(),
    onSuccess: (data, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({
        queryKey: acknowledgementsQueryKeys.org(),
      })
      onSuccess?.(data, variables, onMutateResult, context)
    },
    ...restOptions,
  })
}
